import { useState } from 'react'

import { useClipboard } from 'use-clipboard-copy'

import { useNetwork, usePost } from '@tribeplatform/react-sdk/hooks'
import { Divider } from '@tribeplatform/react-ui-kit/Divider'
import { SpinnerIcon } from '@tribeplatform/react-ui-kit/icons'
import { Input } from '@tribeplatform/react-ui-kit/Input'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { Select } from '@tribeplatform/react-ui-kit/Select'
import { Slider } from '@tribeplatform/react-ui-kit/Slider'
import { Toggle } from '@tribeplatform/react-ui-kit/Toggle'

import { ErrorBoundary } from '../Error/ErrorBoundry'

const toQueryString = obj =>
  Object.keys(obj)
    .map(key => `${key}=${obj[key]}`)
    .join('&')

const getIframe = (iframeSrc, { height, title }) => {
  return `<iframe frameborder="no" src="${iframeSrc}" height="${height}px" width="100%" title="${title}" />`
}

const Views = {
  all: 'Content and Replies',
  'content-only': 'Content Only',
  'replies-only': 'Replies Only',
}

export const PostEmbedCreator = ({ postId }) => {
  const [copied, setCopied] = useState(false)
  const clipboard = useClipboard()
  const { data: post, isFetching: fetchingPost } = usePost({ id: postId })
  const { data: network, isFetching: fetchingNetwork } = useNetwork()
  const loading = fetchingNetwork || fetchingPost
  const baseUrl = `https://${network.domain}/embed/post/${postId}`
  const [iframeHeight, setIframeHeight] = useState(360)
  const [iframeSrcParams, setIframeSrcParams] = useState({
    view: 'all',
    readonly: true,
    show_full_content: true,
  })

  const iframeSrc = `${baseUrl}?${toQueryString(iframeSrcParams)}`
  const set = (key, value) => {
    setIframeSrcParams({
      ...iframeSrcParams,
      [key]: value,
    })
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center w-full h-96">
        <SpinnerIcon className="animate-spin absolute h-9 w-9" />
      </div>
    )
  }

  const iframeToCopy = getIframe(iframeSrc, {
    height: iframeHeight,
    title: post.title,
  })
  return (
    <>
      <Input
        onClick={e => e.currentTarget.select()}
        value={iframeToCopy}
        readOnly
        trailingAddon={
          <Link
            onClick={() => {
              clipboard.copy(iframeToCopy)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            }}
            variant="neutral"
          >
            {copied ? 'Copied' : 'Copy'}
          </Link>
        }
      />
      <Divider />
      <div className="mb-4 space-y-5">
        <div className="flex justify-between items-center">
          <h5>View</h5>
          <Select
            value={iframeSrcParams.view}
            onChange={newValue => {
              set('view', newValue)
            }}
          >
            <Select.Button>{Views[iframeSrcParams.view]}</Select.Button>
            <Select.Items>
              {Object.keys(Views).map(v => (
                <Select.Item key={v} value={v}>
                  {Views[v]}
                </Select.Item>
              ))}
            </Select.Items>
          </Select>
        </div>
        {/* <div className="flex justify-between items-center">
          <h5>Show full content</h5>
          <Toggle
            checked={iframeSrcParams.show_full_content}
            onChange={() => {
              set('show_full_content', !iframeSrcParams.show_full_content)
            }}
          />
        </div> */}
        <div className="flex justify-between items-center">
          <h5>Read only</h5>
          <Toggle
            checked={iframeSrcParams.readonly}
            onChange={() => {
              set('readonly', !iframeSrcParams.readonly)
            }}
          />
        </div>
      </div>
      <h5>Embed height: {iframeHeight}px</h5>
      <div className="mb-2">
        <Slider
          value={iframeHeight}
          step={10}
          min={200}
          max={1000}
          onChange={setIframeHeight}
        />
      </div>
      <ErrorBoundary>
        <h5 className="mb-1">Embed preview</h5>
        <div className="p-3 bg-surface-300 overflow-auto h-96 rounded-md">
          <iframe
            src={iframeSrc}
            height={`${iframeHeight}px`}
            width="100%"
            title={post.title}
          />
        </div>
      </ErrorBoundary>
    </>
  )
}
